import type { Customer, PaymentMethod } from '@/types/checkout';
import type { PaymentMethodEnum, ProviderEnum } from '@/types/common';
import { getBrowserInfo } from '@/utils/browser';
import { getEnvVariable } from '@/utils/get-env-variable';
import { logError } from '@/utils/logger';

/**
 * Last stop before a finalize payload leaves the browser, for every PSP and
 * payment method.
 *
 * `browserInfo` is unconditional (ACDEV-28168): the retry engine can re-route a
 * Stripe-initiated payment to Adyen server-side, long after the client is done,
 * and Adyen rejects a browser-channel payment without it (`15_002`). The client
 * can't know the eventual PSP, so every payload carries it and the PSPs that
 * don't need it ignore it.
 *
 * A PSP-supplied `browserInfo` wins over ours — Adyen's SDK fills it during
 * `onSubmit` with the values behind its own 3DS2 fingerprint. Merging rather
 * than choosing also backfills the handlers that fall back to an empty object
 * when the SDK ref never got populated.
 */
export const enrichFinalizeParams = (params: unknown, forterToken: string) => {
  const request = (params ?? {}) as Record<string, unknown>;
  const innerParams = (request.params ?? {}) as Record<string, unknown>;
  const pspBrowserInfo = (innerParams.browserInfo ?? {}) as Record<string, unknown>;

  return {
    ...request,
    params: {
      ...innerParams,
      fraudDetection: { forterTokenCookie: forterToken },
      browserInfo: { ...getBrowserInfo(), ...pspBrowserInfo },
    },
  };
};

export const getPaypalScriptOptions = (paypalPk: string, currencyCode: string) => {
  if (!paypalPk) {
    logError('PayPal key is missing');
    return null;
  }

  return {
    clientId: getEnvVariable('VITE_PAYPAL_CLIENT_ID', 'test'),
    dataClientToken: paypalPk,
    disableFunding: 'card,venmo,credit,paylater,bancontact,blik,eps,giropay,ideal,mybank,p24,sepa,sofort,mercadopago',
    enableFunding: 'paypal',
    currency: currencyCode.toUpperCase(),
  };
};

export const getLastFourDigits = (cardNumber: string) => {
  return cardNumber?.slice(-4);
};

export const getBillingDetails = (customer: Customer) => {
  return {
    email: customer.email ?? '',
    address: {
      city: customer.address?.city,
      state: customer.address?.state,
      postal_code: customer.address?.zipCode,
      country: customer.geoLocator?.countryCode2,
    },
  };
};

export const getProviderFromPaymentMethod = (paymentMethod: PaymentMethodEnum, paymentMethods: PaymentMethod[]) => {
  return paymentMethods.find((pm) => pm.paymentMethodName === paymentMethod)?.provider as ProviderEnum;
};
